import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Form } from 'redux-form'
import { Button, Grid, Icon, NetworkIcon, Typography } from 'gu-ui-kit'
import PageHeader from 'components/PageHeader'
import Dot from 'components/Dot'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useTranslation } from 'react-i18next'
import SERVICES_API_V1 from 'api/services/v1'
import { ACCOUNT_CREATE_CONNECT_MANUAL_LOAD, SET_NETWORK_DATA } from 'modules/Services/types'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import SidePanel from 'modules/Services/AccountCreate/subcomponents/SidePanel'
import AdvertiserInfoGroup from 'modules/Forms/Account/Create/subcomponents/AdvertiserInfoGroup'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/SubmitButtons'

const FormFrame = ({
  children,
  onSubmit,
  multiLogins,
  form,
  submitFailed,
  submitAccountData,
  submitting,
  onClose,
  advertiser_info = undefined,
}) => {
  const { t, i18n: { language } = {} } = useTranslation()
  const [sidePanelShown, setSidePanelShown] = useState(false)
  const {
    params: { i18n, networkType },
  } = useRouteMatch()

  const { connectManualText: manual = null, networks = [] } = useSelector((state) => ({
    ...state['services/account-create'],
    ...state['services/network'],
    ...state['services/list'],
  }))

  const { homepage_alias = '' } = useMemo(
    () => networks?.find(({ type }) => type === networkType) ?? {},
    [networkType, networks],
  )
  const { networkId } = useNetworksData(networkType)
  const breakpoints = useBreakpoint()
  const dispatch = useDispatch()
  const pathGen = useGeneratePathI18n()

  useEffect(() => {
    if (!networkId) {
      return
    }

    if (multiLogins) {
      SERVICES_API_V1.getNetworkSettings({ networkId }).then(({ response: { data } }) => {
        if (!data?.bulk_account_creation_allowed) {
          return history.push(
            pathGen(ROUTES.SERVICES_NETWORK, {
              networkType,
            }),
          )
        }

        dispatch({
          type: SET_NETWORK_DATA,
          payload: {
            bulk_account_creation_allowed: !!data?.bulk_account_creation_allowed,
          },
        })
      })
    }

    dispatch({
      type: ACCOUNT_CREATE_CONNECT_MANUAL_LOAD,
      payload: {
        networkId,
        params: { lang: i18n },
      },
    })
  }, [networkId])

  return (
    <>
      <Form
        onSubmit={onSubmit}
        noValidate
      >
        <Grid
          alignItems={'start'}
          wrap={false}
          justifyContent={'start'}
          gap={!breakpoints.fb ? 16 : 24}
          className={'layout-tile margin-bottom-16'}
        >
          <NetworkIcon
            width={!breakpoints.fb ? 34 : 44}
            height={!breakpoints.fb ? 34 : 44}
            name={networkType}
            rounded
          />
          <Grid
            direction={'column'}
            gap={8}
          >
            <PageHeader>{t('services::networks.' + networkType)}</PageHeader>
            <Grid
              alignItems={'center'}
              wrap={false}
            >
              <Grid
                i18nKey={'services::forms.register_account.about_network'}
                component={'a'}
                direction={'row'}
                alignItems={'center'}
                fluid={false}
                gap={8}
                wrap={false}
                target={'_blank'}
                href={`${language === 'ru' ? '' : '/en'}/networks/${homepage_alias}`}
                rel="noopener noreferrer"
              >
                <Typography
                  variant={'Body-15'}
                  color={'Label-Secondary'}
                >
                  {t('services::forms.register_account.about_network')}
                </Typography>

                <Icon
                  name={'follow-link'}
                  color={'Interface-Seventh'}
                  size={'s'}
                />
              </Grid>
              {manual?.text && (
                <>
                  <Dot
                    size={'s'}
                    color={'Interface-Fifth'}
                  />
                  <Grid
                    direction={'row'}
                    alignItems={'center'}
                    gap={8}
                    wrap={false}
                    fluid={false}
                    component={Button}
                    variant={'ghost'}
                    onClick={() => setSidePanelShown(true)}
                    link
                  >
                    <Typography
                      variant={'Body-15'}
                      color={'Label-GU-Primary'}
                    >
                      {t('services::forms.register_account.instruction')}
                    </Typography>
                    <Icon
                      name={'document'}
                      color={'Interface-GU-Primary'}
                      size={'s'}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        <div className={'layout-tile margin-bottom-16'}>
          <Typography
            variant={'Subtitle-30'}
            color="Label-Primary"
            className={'margin-bottom-8'}
            component={'h3'}
          >
            {t('services::forms.register_account.form_subtitle')}
          </Typography>
          <Typography
            variant={'Body-15'}
            color="Label-Secondary"
            component={'p'}
            className={'margin-bottom-24'}
          >
            {t('services::forms.register_account.form_summary')}
          </Typography>

          {children}
        </div>

        {advertiser_info !== undefined ? (
          <AdvertiserInfoGroup
            builtInto={true}
            className={'layout-tile'}
            form={form}
            networkType={networkType}
            submitFailed={submitFailed}
            hideable={networkType === 'telegram-ads'}
            actionButtons={
              <SubmitButtons
                formName={form}
                submitted={submitAccountData.submitted}
                submitting={submitting}
                onClose={onClose}
              />
            }
          />
        ) : (
          <SubmitButtons
            formName={form}
            submitted={submitAccountData.submitted}
            submitting={submitting}
            onClose={onClose}
          />
        )}
      </Form>
      {manual && (
        <SidePanel
          shown={sidePanelShown}
          onClose={() => setSidePanelShown(false)}
        >
          <>
            <Typography
              className={'margin-bottom-32'}
              component={'h3'}
              variant={!breakpoints.fb ? 'Header-40' : 'Header-60'}
            >
              {manual.header}
            </Typography>
            <Typography
              variant={'Body-20'}
              component={'div'}
              dangerouslySetInnerHTML={{
                __html: manual.text,
              }}
            />
          </>
        </SidePanel>
      )}
    </>
  )
}

FormFrame.propTypes = {
  children: PropTypes.node,
  onSubmit: PropTypes.func,
  multiLogins: PropTypes.bool,
  form: PropTypes.string,
  advertiser_info: PropTypes.object,
  submitFailed: PropTypes.bool,
  submitAccountData: PropTypes.object,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
}

export default FormFrame
